import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Banknote, Plus, X, Pencil, Trash2, Search, ShieldAlert } from "lucide-react";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const fd = (s) => s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const EMPTY = { customer_name: "", cheque_no: "", cheque_date: "", amount: "", bank_name: "", collected_date: "", collector_name: "", remarks: "", status: "pending" };

const STATUSES = ["pending", "deposited", "cleared", "bounced"];
const STATUS_BADGE = {
  pending: "bg-amber-500/10 text-amber-600",
  deposited: "bg-blue-500/10 text-blue-600",
  cleared: "bg-emerald-500/10 text-emerald-600",
  bounced: "bg-rose-500/10 text-rose-600",
};

const DUE_AFTER_DAYS = 3;
const daysPending = (c) => {
  const ref = c.collected_date || c.cheque_date || c.created_at;
  if (!ref) return 0;
  const d = new Date(ref);
  if (isNaN(d)) return 0;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
};

export default function ChequeEntry() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [modal, setModal] = useState(null); // null | {mode:'new'} | {mode:'edit', item}
  const [err, setErr] = useState("");

  const load = () => api.get("/cheques").then((r) => setItems(r.data)).catch((e) => setErr(formatApiError(e)));
  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    let list = items;
    if (statusFilter) list = list.filter((c) => (c.status || "pending") === statusFilter);
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((c) => `${c.customer_name} ${c.cheque_no} ${c.bank_name || ""} ${c.collector_name || ""}`.toLowerCase().includes(s));
    }
    return list;
  }, [items, q, statusFilter]);

  const total = useMemo(() => filtered.reduce((a, c) => a + Number(c.amount || 0), 0), [filtered]);
  const pendingTotal = useMemo(() => items.filter((c) => ["pending", "deposited"].includes(c.status || "pending")).reduce((a, c) => a + Number(c.amount || 0), 0), [items]);

  const setStatus = async (c, status) => {
    try { await api.patch(`/cheques/${c.id}`, { status }); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  if (user?.role === "company_admin") {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center px-4 py-24 text-center">
        <ShieldAlert className="h-12 w-12 text-destructive" />
        <h1 data-testid="cheque-access-denied" className="mt-4 font-heading text-2xl font-bold">Access Denied</h1>
        <p className="mt-2 text-sm text-muted-foreground">Cheque Entry is not available for Company Admin accounts.</p>
      </div>
    );
  }

  const del = async (c) => {
    if (!window.confirm(`Delete cheque ${c.cheque_no} of ${c.customer_name}?`)) return;
    try { await api.delete(`/cheques/${c.id}`); await load(); } catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-eyebrow text-primary">Payments</p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight flex items-center gap-2"><Banknote className="h-7 w-7" /> Cheque Entry</h1>
          <p className="mt-1 text-sm text-muted-foreground">Track customer cheques — number, date, amount, bank and collection details.</p>
        </div>
        <button data-testid="cheque-new" onClick={() => setModal({ mode: "new" })}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90">
          <Plus className="h-3.5 w-3.5" /> New cheque
        </button>
      </header>
      {err && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="cheque-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer, cheque no, bank…"
            className="w-72 rounded-md border border-input bg-background py-2 pl-8 pr-3 text-sm" />
        </div>
        <span data-testid="cheque-total" className="rounded-md border border-border/60 px-3 py-2 text-xs font-semibold">
          {filtered.length} cheque{filtered.length !== 1 ? "s" : ""} · Total {inr(total)}
        </span>
        <span data-testid="cheque-pending-total" className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-600">
          Not yet cleared: {inr(pendingTotal)}
        </span>
        <div className="flex gap-1">
          {["", ...STATUSES].map((s) => (
            <button key={s || "all"} data-testid={`cheque-status-filter-${s || "all"}`} onClick={() => setStatusFilter(s)}
              className={`rounded-md border px-2.5 py-1.5 text-xs font-medium capitalize ${statusFilter === s ? "border-primary bg-primary/10 text-primary" : "border-border/70 text-muted-foreground hover:bg-secondary"}`}>
              {s || "All"}
            </button>
          ))}
        </div>
      </div>

      <div className="sharp-card overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-secondary/40 text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5">Customer</th>
              <th className="px-4 py-2.5">Cheque No</th>
              <th className="px-4 py-2.5">Cheque Date</th>
              <th className="px-4 py-2.5">Amount</th>
              <th className="px-4 py-2.5">Bank</th>
              <th className="px-4 py-2.5">Collected</th>
              <th className="px-4 py-2.5">Collector</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">Remarks</th>
              <th className="px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/40">
            {filtered.map((c) => (
              <tr key={c.id} data-testid={`cheque-row-${c.id}`} className="hover:bg-secondary/30">
                <td className="px-4 py-2 font-medium">{c.customer_name}</td>
                <td className="px-4 py-2">{c.cheque_no}</td>
                <td className="px-4 py-2 text-xs">{fd(c.cheque_date)}</td>
                <td className="px-4 py-2 font-semibold text-emerald-600">{inr(c.amount)}</td>
                <td className="px-4 py-2 text-xs">{c.bank_name || "—"}</td>
                <td className="px-4 py-2 text-xs">{fd(c.collected_date)}</td>
                <td className="px-4 py-2 text-xs">{c.collector_name || "—"}</td>
                <td className="px-4 py-2">
                  <select data-testid={`cheque-status-${c.id}`} value={c.status || "pending"} onChange={(e) => setStatus(c, e.target.value)}
                    className={`rounded-md border-0 px-2 py-1 text-[11px] font-semibold uppercase tracking-wider ${STATUS_BADGE[c.status || "pending"]}`}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {(c.status || "pending") === "pending" && daysPending(c) >= DUE_AFTER_DAYS && (
                    <div data-testid={`cheque-due-${c.id}`} className="mt-1 inline-flex rounded-md bg-rose-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-rose-600">
                      {daysPending(c)}d · deposit due
                    </div>
                  )}
                </td>
                <td className="max-w-[200px] truncate px-4 py-2 text-xs text-muted-foreground" title={c.remarks || ""}>{c.remarks || "—"}</td>
                <td className="px-4 py-2">
                  <div className="flex gap-1">
                    <button data-testid={`cheque-edit-${c.id}`} onClick={() => setModal({ mode: "edit", item: c })}
                      className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-primary"><Pencil className="h-3.5 w-3.5" /></button>
                    <button data-testid={`cheque-delete-${c.id}`} onClick={() => del(c)}
                      className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={10} className="px-4 py-10 text-center text-xs text-muted-foreground">No cheques recorded yet. Click &ldquo;New cheque&rdquo; to add one.</td></tr>}
          </tbody>
        </table>
      </div>

      {modal && <ChequeModal customers={customers} existing={modal.item} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

function ChequeModal({ customers, existing, onClose, onSaved }) {
  const [f, setF] = useState(existing ? {
    customer_name: existing.customer_name || "", cheque_no: existing.cheque_no || "",
    cheque_date: existing.cheque_date || "", amount: existing.amount ?? "",
    bank_name: existing.bank_name || "", collected_date: existing.collected_date || "",
    collector_name: existing.collector_name || "", remarks: existing.remarks || "",
    status: existing.status || "pending",
  } : EMPTY);
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = { ...f, amount: Number(f.amount) || 0 };
      if (existing) await api.patch(`/cheques/${existing.id}`, payload);
      else await api.post("/cheques", payload);
      onSaved();
    } catch (er) { setErr(formatApiError(er)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl sharp-card max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <h3 className="font-heading text-lg font-semibold">{existing ? `Edit cheque ${existing.cheque_no}` : "New cheque entry"}</h3>
          <button data-testid="cheque-modal-close" onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="grid gap-3 p-5 sm:grid-cols-2">
          <div>
            <label className="label-eyebrow mb-1.5 block">Customer name *</label>
            <input data-testid="cheque-form-customer" required list="cheque-customers" value={f.customer_name} onChange={set("customer_name")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            <datalist id="cheque-customers">
              {customers.map((c) => <option key={c.id} value={c.company_name} />)}
            </datalist>
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Cheque No *</label>
            <input data-testid="cheque-form-no" required value={f.cheque_no} onChange={set("cheque_no")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Cheque date</label>
            <input data-testid="cheque-form-date" type="date" value={f.cheque_date} onChange={set("cheque_date")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Amount (₹) *</label>
            <input data-testid="cheque-form-amount" required type="number" min="0" step="0.01" value={f.amount} onChange={set("amount")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Cheque bank name</label>
            <input data-testid="cheque-form-bank" value={f.bank_name} onChange={set("bank_name")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Cheque collected date</label>
            <input data-testid="cheque-form-collected" type="date" value={f.collected_date} onChange={set("collected_date")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Cheque collector name</label>
            <input data-testid="cheque-form-collector" value={f.collector_name} onChange={set("collector_name")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Status</label>
            <select data-testid="cheque-form-status" value={f.status} onChange={set("status")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm capitalize">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Remarks</label>
            <textarea data-testid="cheque-form-remarks" rows={2} value={f.remarks} onChange={set("remarks")} className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          {err && <div className="sm:col-span-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          <div className="sm:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm font-semibold hover:bg-secondary">Cancel</button>
            <button data-testid="cheque-form-submit" disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {busy ? "Saving…" : existing ? "Save changes" : "Add cheque"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
